<template>
    <div v-if="mats.length > 0">
        <div class="buttons">
            <ToggleableButton
                v-for="mat in mats"
                :key="mat.id"
                :ref="el => (toggleButtons[toggleButtonRefName(mat.id)] = el)"
                :id="mat.id"
                :text="mat.name"
                :initial-state="isToggledInLocation(mat.id)"
                class="button"
                @stateChange="selectedMat"
            />
            <button class="button is-light" v-if="showSelectAll" @click="selectAllMats">
                <span>{{ translate("common.select_all") }}</span>
            </button>
        </div>
    </div>
    <div v-else-if="requestsInProgress > 0">
        <LoadingSpinner2 />
    </div>
    <div v-else-if="mats.length === 0 && requestsInProgress === 0">
        <span class="text-warning has-text-warning">{{ translate("admin_fights_manage.no_mats") }}</span>
    </div>
</template>

<script setup lang="ts">
    import { ref, onMounted } from "vue";
    import ToggleableButton from "./ToggleableButton.vue";
    import { translate } from "./translations";
    import { handleError } from "./errorHandler";
    import LoadingSpinner2 from "./LoadingSpinner2.vue";
    import { Mat } from "./types";
    import matsApi from "../Api/mats";

    const props = defineProps<{
        eventId: number;
        preloadedMats?: Array<Mat>;
        showSelectAll?: boolean;
    }>();
    const emit = defineEmits<{
        (e: "initialized"): void;
        (e: "selectedMat", event: SelectedMatEvent[]): void;
    }>();

    const mats = ref<Array<Mat>>([]);
    const matsLoaded = ref(false);
    const requestsInProgress = ref(0);
    const toggleButtons = ref({});

    const fetchAllMats = () => {
        if (props.preloadedMats && props.preloadedMats.length > 0) {
            mats.value = props.preloadedMats;
            return Promise.resolve();
        }

        requestsInProgress.value++;

        return matsApi
            .getMatsListing(props.eventId)
            .then(matsResponse => {
                mats.value = matsResponse;
                matsLoaded.value = true;
            })
            .catch(handleError)
            .finally(() => {
                requestsInProgress.value--;
            });
    };

    const getSelectedMats = () => {
        return mats.value
            .filter(mat => {
                const btn = toggleButtons.value[toggleButtonRefName(mat.id)];
                return btn.isEnabled();
            })
            .map(mat => mat.id);
    };

    const selectMats = matIds => {
        if (!Array.isArray(matIds)) {
            return;
        }

        matIds = matIds.map(matId => parseInt(matId));

        mats.value.forEach(mat => {
            const btn = toggleButtons.value[toggleButtonRefName(mat.id)];
            const enabled = matIds.includes(mat.id);
            btn.setState(enabled);
        });
    };

    export type SelectedMatEvent = {
        matId: number;
        state: boolean;
        buttonReference: any;
    };

    function selectAllMats() {
        const eventData = new Array<SelectedMatEvent>();
        mats.value.forEach(mat => {
            const btn = toggleButtons.value[toggleButtonRefName(mat.id)];
            //We don't trigger event here, it's going to happen after the loop. Once is enough.
            btn.setState(true, false);

            eventData.push({
                matId: mat.id,
                state: true,
                buttonReference: btn,
            });
        });

        emit("selectedMat", eventData);
    }

    defineExpose({
        getSelectedMats,
        selectMats,
    });

    const isToggledInLocation = matId => {
        const idsInLocation = getMatIdsFromLocation();
        return idsInLocation.indexOf(matId.toString()) >= 0;
    };

    const selectedMat = (matId: number, state: boolean) => {
        const matButton = toggleButtons.value[toggleButtonRefName(matId)];
        emit("selectedMat", [
            {
                matId: matId,
                state: state,
                buttonReference: matButton,
            },
        ]);
    };

    const toggleButtonRefName = (matId: number) => {
        return "matButton_" + matId;
    };

    const getMatIdsFromLocation = () => {
        const params = new URLSearchParams(window.location.search);
        const mat = params.get("mat") || "";
        return mat.split(",");
    };

    onMounted(() => {
        fetchAllMats()
            .then(() => {
                emit("initialized");
            })
            .catch(handleError);
    });
</script>
